import { Capacitor } from "@capacitor/core";
import type { NativeSession, NativeSessionStore } from "@pontimed/auth-client";

/**
 * Where the native app keeps its session pair (@pontimed/auth-client
 * native-session.ts) — 2026-10-10.
 *
 * The Capacitor build cannot hold apps/api's session cookies (third-party in
 * its WebView), so the pair a sign-in or refresh answers with is kept here
 * and sent back by hand: the access token as `Authorization: Bearer`, the
 * refresh token in the body of /api/auth/refresh and /refresh/logout.
 *
 * Kept in the WebView's own storage — the app's private origin
 * (`capacitor://localhost`), sandboxed per app on the device, never shared
 * with a browser — and kept OUT of device backups and device-to-device
 * transfers (scripts/harden-native-storage.mjs, run by the `capacitor:*:after`
 * hooks): a restored copy of a live refresh token would trip reuse detection
 * on its first refresh and end every session of the account.
 *
 * PRE-RELEASE REQUIREMENT: it is plaintext in the WebView's storage, readable
 * on a rooted / jailbroken device. Before the native app ships, move it to a
 * Keychain / Keystore-backed plugin with a this-device-only accessibility
 * (see the 2026-10-10 review notes) — a native dependency this store can
 * switch to without changing its callers.
 *
 * undefined on the web build: cookies carry the session there.
 */
const STORAGE_KEY = "pm:native-session";

function isSession(value: unknown): value is NativeSession {
  if (!value || typeof value !== "object") return false;
  const { accessToken, refreshToken } = value as Record<string, unknown>;
  return typeof accessToken === "string" && typeof refreshToken === "string";
}

export function nativeSessionStore(): NativeSessionStore | undefined {
  if (!Capacitor.isNativePlatform()) return undefined;
  return {
    read: () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : null;
        return isSession(parsed) ? parsed : null;
      } catch {
        return null;
      }
    },
    // A failed write throws: a sign-in whose session cannot be kept must fail
    // where the user sees it, not on the next request.
    write: (session) => localStorage.setItem(STORAGE_KEY, JSON.stringify(session)),
    clear: () => {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* nothing stored */
      }
    },
  };
}
